import { motion } from 'framer-motion';
import {
  ChevronDown,
  Heart,
  Menu,
  Package,
  ShoppingCart,
  User,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import MicronLogo from '../../components/website/MicronLogo';
import HomeBannerSlider from '../../components/website/HomeBannerSlider';
import HomeCategoryNav from '../../components/website/HomeCategoryNav';
import { useHomeCategories } from '../../hooks/useHomeCategories';
import {
  categorySections,
  dealProducts,
  navLinks,
} from './homeData';

function useCountdown(target: Date) {
  const calc = () => {
    const diff = Math.max(0, target.getTime() - Date.now());
    return {
      days: Math.floor(diff / 86400000),
      hours: Math.floor((diff % 86400000) / 3600000),
      minutes: Math.floor((diff % 3600000) / 60000),
      seconds: Math.floor((diff % 60000) / 1000),
    };
  };

  const [time, setTime] = useState(calc);

  useEffect(() => {
    const id = setInterval(() => setTime(calc()), 1000);
    return () => clearInterval(id);
  }, [target]);

  return time;
}

function CountdownBox({ value, label }: { value: number; label: string }) {
  return (
    <div className="flex flex-col items-center">
      <motion.div
        key={value}
        initial={{ y: -8, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.3 }}
        className="flex h-14 w-16 items-center justify-center rounded-lg bg-micron-black text-xl font-bold text-white sm:h-16 sm:w-[4.5rem] sm:text-2xl"
      >
        {String(value).padStart(2, '0')}
      </motion.div>
      <span className="mt-1.5 text-xs text-neutral-500">{label}</span>
    </div>
  );
}

function ProductGridCard({ name, price, image }: { name: string; price: number; image: string }) {
  return (
    <motion.article
      whileHover={{ y: -2, boxShadow: '0 4px 12px rgba(215,25,32,0.08)' }}
      className="flex cursor-pointer items-center gap-3 rounded-lg border border-neutral-200 bg-white p-3 transition-colors hover:border-red-200"
    >
      <img src={image} alt={name} className="h-16 w-16 shrink-0 rounded-md object-cover" />
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-micron-black">{name}</p>
        <p className="mt-0.5 text-xs text-neutral-500">
          From <span className="font-semibold text-micron-black">USD {price}</span>
        </p>
      </div>
    </motion.article>
  );
}

function DealCard({ name, discount, image }: { name: string; discount: number; image: string }) {
  return (
    <motion.article
      whileHover={{ y: -4 }}
      className="group relative flex min-w-[140px] flex-1 flex-col overflow-hidden rounded-lg border border-neutral-200 bg-white sm:min-w-0"
    >
      <div className="flex flex-1 items-center justify-center bg-micron-gray p-4">
        <motion.img
          src={image}
          alt={name}
          className="h-28 w-full object-contain"
          whileHover={{ scale: 1.05 }}
          transition={{ duration: 0.3 }}
        />
      </div>
      <div className="border-t border-neutral-100 px-3 py-2.5">
        <p className="truncate text-xs font-medium text-neutral-700">{name}</p>
      </div>
      <span className="absolute bottom-10 left-2 rounded bg-micron-red px-1.5 py-0.5 text-[10px] font-bold text-white">
        -{discount}%
      </span>
    </motion.article>
  );
}

function CategorySection({
  title,
  featureImage,
  products,
}: {
  title: string;
  featureImage: string;
  products: { name: string; price: number; image: string }[];
}) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.5 }}
      className="mb-6"
    >
      <div className="grid gap-4 lg:grid-cols-[240px_1fr]">
        <div className="relative overflow-hidden rounded-lg">
          <img src={featureImage} alt={title} className="h-48 w-full object-cover lg:h-full lg:min-h-[320px]" />
          <div className="absolute inset-0 bg-gradient-to-t from-micron-black/70 to-transparent" />
          <div className="absolute bottom-0 left-0 p-5 text-white">
            <h3 className="text-lg font-bold leading-snug">{title}</h3>
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
              type="button"
              className="mt-3 rounded-md bg-white px-4 py-2 text-sm font-semibold text-micron-red shadow-sm"
            >
              Explore all
            </motion.button>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {products.map((p) => (
            <ProductGridCard key={p.name} {...p} />
          ))}
        </div>
      </div>
    </motion.section>
  );
}

export default function Home() {
  const { categories, loading: categoriesLoading, activeId, setActiveId } = useHomeCategories();
  const countdownTarget = new Date(Date.now() + 4 * 86400000 + 13 * 3600000 + 34 * 60000 + 56 * 1000);
  const countdown = useCountdown(countdownTarget);

  return (
    <div className="min-h-screen bg-micron-gray font-['Plus_Jakarta_Sans',sans-serif] text-micron-black">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-neutral-200 bg-white">
        <div className="mx-auto flex max-w-[1320px] flex-wrap items-center gap-4 px-4 py-3 sm:px-6">
          <MicronLogo className="h-11 w-auto" />

          <div className="flex min-w-[200px] flex-1 items-center gap-0 overflow-hidden rounded-lg border border-micron-red">
            <input
              type="search"
              placeholder="Find product"
              className="min-w-0 flex-1 bg-white px-4 py-2.5 text-sm outline-none placeholder:text-neutral-400"
            />
            <div className="hidden items-center gap-1 border-l border-neutral-200 px-3 text-sm text-neutral-600 sm:flex">
              All category
              <ChevronDown className="h-4 w-4" />
            </div>
            <motion.button
              whileHover={{ backgroundColor: '#B8141A' }}
              whileTap={{ scale: 0.98 }}
              type="button"
              className="bg-micron-red px-5 py-2.5 text-sm font-semibold text-white"
            >
              Search
            </motion.button>
          </div>

          <div className="hidden items-center gap-5 lg:flex">
            {[
              { icon: Package, label: 'Orders' },
              { icon: Heart, label: 'Saved' },
              { icon: ShoppingCart, label: 'My cart' },
            ].map(({ icon: Icon, label }) => (
              <button
                key={label}
                type="button"
                className="flex flex-col items-center gap-0.5 text-neutral-600 transition hover:text-micron-red"
              >
                <Icon className="h-5 w-5" />
                <span className="text-xs">{label}</span>
              </button>
            ))}
            <Link to="/signin" className="flex flex-col items-center gap-0.5 text-neutral-600 transition hover:text-micron-red">
              <User className="h-5 w-5" />
              <span className="text-xs">Sign in</span>
            </Link>
          </div>
        </div>

        <div className="border-t border-neutral-100 bg-white">
          <div className="mx-auto flex max-w-[1320px] items-center justify-between px-4 py-2.5 sm:px-6">
            <div className="flex items-center gap-6">
              <button
                type="button"
                className="flex items-center gap-2 rounded-md bg-micron-red px-4 py-2 text-sm font-semibold text-white transition hover:bg-micron-red-dark"
              >
                <Menu className="h-4 w-4" />
                All category
              </button>
              <nav className="hidden items-center gap-5 md:flex">
                {navLinks.map((link) => (
                  <a
                    key={link}
                    href="#"
                    className="flex items-center gap-1 text-sm text-neutral-600 transition hover:text-micron-red"
                  >
                    {link}
                    {link === 'Help' && <ChevronDown className="h-3.5 w-3.5" />}
                  </a>
                ))}
              </nav>
            </div>
            <button type="button" className="hidden items-center gap-2 text-sm text-neutral-600 sm:flex">
              <span className="text-base">🇱🇰</span>
              Sri Lanka, LKR
              <ChevronDown className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1320px] px-4 py-4 sm:px-6">
        <HomeCategoryNav
          variant="mobile-strip"
          categories={categories}
          loading={categoriesLoading}
          activeId={activeId}
          onSelect={setActiveId}
          className="mb-4"
        />

        <div className="grid gap-4 lg:grid-cols-[220px_1fr]">
          <HomeCategoryNav
            variant="sidebar"
            categories={categories}
            loading={categoriesLoading}
            activeId={activeId}
            onSelect={setActiveId}
          />

          <HomeBannerSlider autoPlayMs={5500} />
        </div>

        <motion.section
          id="deals"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mt-6 rounded-lg border border-neutral-200 bg-white p-4 sm:p-5"
        >
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-lg font-bold text-micron-black">Deals and offers</h2>
            <div className="flex items-center gap-2 sm:gap-3">
              <CountdownBox value={countdown.days} label="Days" />
              <CountdownBox value={countdown.hours} label="Hour" />
              <CountdownBox value={countdown.minutes} label="Min" />
              <CountdownBox value={countdown.seconds} label="Sec" />
            </div>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-1 sm:grid sm:grid-cols-5 sm:overflow-visible">
            {dealProducts.map((p) => (
              <DealCard key={p.name} {...p} />
            ))}
          </div>
        </motion.section>

        <div className="mt-6 space-y-6">
          {categorySections.map((section) => (
            <CategorySection
              key={section.title}
              title={section.title}
              featureImage={section.featureImage}
              products={section.products}
            />
          ))}
        </div>
      </main>

      <footer className="mt-8 border-t border-neutral-200 bg-white">
        <div className="mx-auto max-w-[1320px] px-4 py-10 sm:px-6">
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <MicronLogo className="h-12 w-auto" />
              <p className="mt-4 text-sm text-neutral-500">
                brixECOM — your trusted marketplace for premium electronics and smart devices.
              </p>
            </div>
            <div>
              <h4 className="font-bold text-micron-black">Get support</h4>
              <ul className="mt-3 space-y-2 text-sm text-neutral-500">
                <li><a href="#" className="transition hover:text-micron-red">Help Center</a></li>
                <li><a href="#" className="transition hover:text-micron-red">Order status</a></li>
                <li><a href="#" className="transition hover:text-micron-red">Shipping info</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-micron-black">About</h4>
              <ul className="mt-3 space-y-2 text-sm text-neutral-500">
                <li><a href="#" className="transition hover:text-micron-red">About us</a></li>
                <li><a href="#" className="transition hover:text-micron-red">Careers</a></li>
                <li><Link to="/signin" className="transition hover:text-micron-red">Seller login</Link></li>
                <li><Link to="/home2" className="transition hover:text-micron-red">Home v2</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-micron-black">Contact</h4>
              <ul className="mt-3 space-y-2 text-sm text-neutral-500">
                <li>support@brixecom.com</li>
                <li>+94 11 234 5678</li>
              </ul>
            </div>
          </div>
          <p className="mt-8 border-t border-neutral-100 pt-6 text-center text-xs text-neutral-400">
            © {new Date().getFullYear()} brixECOM. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
